"use client";

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import CopilotBotIcon from "./CopilotBotIcon";
import "./copilot.css";
import { useEffectiveOrg } from "@/hooks/useEffectiveOrg";
import { useSession } from "@/context/SessionContext";
import { usePathname } from "next/navigation";
import { getUserDepartments } from "@/utils/departmentUtils";
import { getDepartments } from "@/modules/departments/services/userService";
import {
  X,
  RotateCcw,
  Paperclip,
  CheckCircle2,
  Download,
  FileText,
  FileCheck,
  Sparkles,
  Shield,
  Loader2,
  Check,
  Plus,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Edit2,
  Eye,
  ExternalLink,
  Columns,
  GripVertical,
  LayoutDashboard,
} from "lucide-react";

/**
 * Base64 URL and JWT Helper to maintain compatibility with backend's JwtTenantIsolationFilter
 */
function generateMockJwt(userId, orgId, department, roles) {
  const b64 = (str) =>
    btoa(unescape(encodeURIComponent(str)))
      .replace(/=+$/, "")
      .replace(/\+/g, "-")
      .replace(/\//g, "_");
  const header = b64(JSON.stringify({ alg: "HS256", typ: "JWT" }));
  const effectiveRoles = roles && roles.length ? roles : ["RISK_OWNER"];
  const payload = b64(
    JSON.stringify({
      sub: userId || "user-1",
      organization: orgId || "org-1",
      department: department || null,
      role: effectiveRoles,
    }),
  );
  return `${header}.${payload}.mock-signature`;
}

function escapeHtml(str) {
  return String(str ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c],
  );
}

function parseMarkdownTables(text) {
  if (!text || !text.includes("|")) return { processed: text, tables: [] };

  const tables = [];
  const tableBlockRegex = /((?:^[ \t]*\|.+?\|[ \t]*(?:\r?\n|$))+)/gm;

  const processed = text.replace(tableBlockRegex, (match) => {
    const rawLines = match
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    if (rawLines.length < 2) return match;

    const isSep = /^\|(?:\s*:?-{2,}:?\s*\|)+$/.test(rawLines[1]);
    if (!isSep) return match;

    const parseRow = (row) =>
      row
        .replace(/^\|/, "")
        .replace(/\|$/, "")
        .split("|")
        .map((c) => c.trim());

    const headers = parseRow(rawLines[0]);
    const alignments = parseRow(rawLines[1]).map((s) => {
      const left = s.startsWith(":");
      const right = s.endsWith(":");
      if (left && right) return "text-center";
      if (right) return "text-right";
      return "text-left";
    });

    const formatCell = (str) =>
      escapeHtml(str)
        .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")
        .replace(/\*(.*?)\*/g, "<i>$1</i>")
        .replace(/`([^`]+)`/g, "<code>$1</code>");

    let thead = "<thead><tr>";
    headers.forEach((h, i) => {
      const align = alignments[i] || "text-left";
      thead += `<th class="${align}">${formatCell(h)}</th>`;
    });
    thead += "</tr></thead>";

    let tbody = "<tbody>";
    for (let r = 2; r < rawLines.length; r++) {
      const cells = parseRow(rawLines[r]);
      tbody += "<tr>";
      headers.forEach((_, cIdx) => {
        const cell = cells[cIdx] ?? "";
        const align = alignments[cIdx] || "text-left";
        tbody += `<td class="${align}">${formatCell(cell)}</td>`;
      });
      tbody += "</tr>";
    }
    tbody += "</tbody>";

    const tableHtml = `<div class="overflow-x-auto my-2 rounded-lg border border-slate-200"><table>${thead}${tbody}</table></div>`;
    const token = `__MD_TABLE_${tables.length}__`;
    tables.push(tableHtml);
    return `\n\n${token}\n\n`;
  });

  return { processed, tables };
}

/**
 * Safe URL encoder: never throws on malformed % sequences
 * (encodeURI(decodeURI(x)) throws URIError for names like "100%.pdf").
 */
function safeEncodeUrl(u) {
  try {
    return encodeURI(decodeURI(u));
  } catch (e) {
    try {
      return encodeURI(u);
    } catch (e2) {
      return u;
    }
  }
}

function resolveDocUrl(rawUrl) {
  if (!rawUrl) return "";
  const trimmed = String(rawUrl).trim().replace(/^<|>$/g, "");
  if (/^https?:\/\//i.test(trimmed)) {
    return safeEncodeUrl(trimmed);
  }
  const baseUrl = (
    (typeof process !== "undefined" && process.env.NEXT_PUBLIC_SP) ||
    "https://api.calvant.com"
  )
    .trim()
    .replace(/\/+$/, "");

  let path = trimmed;
  if (path.startsWith("/doc-service/")) {
    // already correct
  } else if (path.startsWith("doc-service/")) {
    path = "/" + path;
  } else if (path.startsWith("/uploads/")) {
    path = "/doc-service" + path;
  } else if (path.startsWith("uploads/")) {
    path = "/doc-service/" + path;
  } else {
    path = "/doc-service/uploads/" + path.replace(/^\/+/, "");
  }

  return baseUrl + safeEncodeUrl(path);
}

function getCleanDocName(filename) {
  if (!filename) return "Document.pdf";
  let name = "";
  try {
    name = decodeURIComponent(String(filename));
  } catch (e) {
    name = String(filename);
  }
  name = name.split("/").pop().trim();
  // Strip timestamp prefixes (e.g. 1782205196251-)
  name = name.replace(/^\d{10,14}-/, "").trim();
  return name || "Document.pdf";
}

function extractReferencedDocs(text) {
  if (!text || typeof text !== "string") return [];
  const foundMap = new Map();

  const addDoc = (rawName, resolvedUrl) => {
    if (!foundMap.has(resolvedUrl)) {
      foundMap.set(resolvedUrl, {
        name: rawName,
        displayName: getCleanDocName(rawName),
        url: resolvedUrl,
      });
    }
  };

  // 1) Markdown links [text](url) - URL may contain spaces
  const mdLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let match;
  while ((match = mdLinkRegex.exec(text)) !== null) {
    const linkText = match[1].trim();
    const linkUrl = match[2].trim().replace(/^<|>$/g, "");
    if (
      /\.pdf/i.test(linkUrl) ||
      linkUrl.includes("/uploads/") ||
      /\.pdf/i.test(linkText)
    ) {
      const rawFilename = linkUrl.split("/").pop() || linkText;
      let cleanFilename = rawFilename;
      try {
        cleanFilename = decodeURIComponent(rawFilename).trim();
      } catch (e) {}
      addDoc(cleanFilename, resolveDocUrl(linkUrl));
    }
  }

  // Remove markdown links so later passes don't re-detect their inner filenames
  let remaining = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, " ");

  // 2) Unlinked timestamped PDF filenames, spaces allowed
  //    (e.g. 1782205196251-Background Verification Policy.pdf)
  const timestampPdfRegex = /\b(\d{10,14}-[a-zA-Z0-9_\-.&()% ]+?\.pdf)\b/gi;
  while ((match = timestampPdfRegex.exec(remaining)) !== null) {
    const rawFname = match[1].replace(/&amp;/g, "&").trim();
    addDoc(rawFname, resolveDocUrl(rawFname));
  }
  // Strip them so the single-word pass can't pick up a broken tail like "Policy.pdf"
  remaining = remaining.replace(timestampPdfRegex, " ");

  // 3) Unlinked standard PDF filenames (no spaces)
  const singlePdfRegex = /\b([a-zA-Z0-9_\-.&%]+\.pdf)\b/gi;
  while ((match = singlePdfRegex.exec(remaining)) !== null) {
    const rawFname = match[1].replace(/&amp;/g, "&").trim();
    addDoc(rawFname, resolveDocUrl(rawFname));
  }

  return Array.from(foundMap.values());
}

function buildDocChipHtml(rawUrlOrName, rawTitle) {
  const docUrl = escapeHtml(resolveDocUrl(rawUrlOrName));
  const cleanTitle = escapeHtml(getCleanDocName(rawTitle));
  return `<span role="button" tabindex="0" data-doc-url="${docUrl}" data-doc-name="${cleanTitle}" class="copilot-doc-chip" title="Click to preview ${cleanTitle}"><span class="copilot-doc-chip-badge">PDF</span><span>${cleanTitle}</span><span class="copilot-doc-chip-action">👁 Preview</span></span>`;
}

function formatMarkdownToHtml(md) {
  if (!md) return "";
  const { processed, tables } = parseMarkdownTables(md);

  // Tokens for links / chips so later regexes can never touch generated HTML
  const links = [];
  const stash = (html) => {
    const token = `__MD_LINK_${links.length}__`;
    links.push(html);
    return token;
  };

  // Extract markdown links [text](url) before escaping - URL may contain spaces
  let workingText = processed.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (match, text, rawUrl) => {
      const cleanUrl = rawUrl.trim().replace(/^<|>$/g, "");
      const isDoc =
        /\.pdf(\?[^)]*)?$/i.test(cleanUrl) ||
        cleanUrl.includes("/uploads/") ||
        cleanUrl.includes("doc-service") ||
        /\.pdf/i.test(text);

      if (isDoc) {
        return stash(buildDocChipHtml(cleanUrl, text));
      }
      const href = /^(https?:|mailto:)/i.test(cleanUrl)
        ? cleanUrl
        : resolveDocUrl(cleanUrl);
      return stash(
        `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" class="text-indigo-600 underline font-medium hover:text-indigo-800">${escapeHtml(text)}</a>`,
      );
    },
  );

  let html = escapeHtml(workingText);

  // Headers
  html = html.replace(/^### (.*$)/gim, "<h3>$1</h3>");
  html = html.replace(/^## (.*$)/gim, "<h2>$1</h2>");
  html = html.replace(/^# (.*$)/gim, "<h1>$1</h1>");

  // Bold and italic
  html = html.replace(/\*\*\*(.*?)\*\*\*/gim, "<b><i>$1</i></b>");
  html = html.replace(/\*\*(.*?)\*\*/gim, "<b>$1</b>");
  html = html.replace(/\*(.*?)\*/gim, "<i>$1</i>");

  // Unlinked timestamped PDF filenames with spaces
  // (e.g. 1782205196251-Background Verification Policy.pdf)
  html = html.replace(
    /\b(\d{10,14}-(?:&amp;|[a-zA-Z0-9_\-.() %])+?\.pdf)\b/gi,
    (match, fname) => {
      const rawFname = fname.replace(/&amp;/g, "&").trim();
      return stash(buildDocChipHtml(rawFname, rawFname));
    },
  );

  // Unlinked single-word PDF filenames
  // (e.g. 1783587493660-CalVant_Integrations&ToolReference_ISO27001_v0.1.pdf)
  html = html.replace(
    /\b((?:&amp;|[a-zA-Z0-9_\-.%])+?\.pdf)\b/gi,
    (match, fnameWithAmp) => {
      const rawFname = fnameWithAmp.replace(/&amp;/g, "&");
      return stash(buildDocChipHtml(rawFname, rawFname));
    },
  );

  // Horizontal rules
  html = html.replace(/^---$/gim, "<hr/>");

  // Code blocks
  html = html.replace(
    /```([\w-]*)\n([\s\S]*?)```/gim,
    "<pre><code>$2</code></pre>",
  );
  html = html.replace(/`([^`]+)`/gim, "<code>$1</code>");

  // Bullet points
  html = html.replace(/^\* (.*$)/gim, "<li>$1</li>");
  html = html.replace(/^- (.*$)/gim, "<li>$1</li>");
  html = html.replace(/^[0-9]+\. (.*$)/gim, "<li>$1</li>");

  // Wrap lists
  html = html.replace(/(<li>.*<\/li>)/gim, "<ul>$1</ul>");

  // Paragraphs / line breaks
  html = html.replace(/\n\n+/gim, "</p><p>");
  html = html.replace(/\n/gim, "<br/>");

  // Re-insert link / chip tokens
  links.forEach((linkHtml, idx) => {
    html = html.replace(`__MD_LINK_${idx}__`, () => linkHtml);
  });

  // Re-insert table tokens cleanly without invalid nested <p>
  tables.forEach((tableHtml, idx) => {
    html = html.replace(`<p>__MD_TABLE_${idx}__</p>`, () => tableHtml);
    html = html.replace(`__MD_TABLE_${idx}__`, () => tableHtml);
  });

  return `<div class="copilot-prose text-[11.5px] leading-relaxed"><p>${html}</p></div>`;
}

/**
 * CAL-69 Parser: Detect and parse the first-time role responsibilities prompt
 * from the live microservice orchestrator.
 */
function parseResponsibilitiesPrompt(text) {
  if (!text || typeof text !== "string") return null;
  const isRespPrompt =
    (text.includes("Please select your responsibilities") ||
      text.includes("apply to you (e.g.,") ||
      text.includes("describe your responsibilities")) &&
    text.includes("[RESP-");
  if (!isRespPrompt) return null;

  const lines = text.split("\n");
  const items = [];
  let header = "";

  for (const line of lines) {
    const trimmed = line.trim();
    const match = trimmed.match(/^(\d+)\.\s*\[([^\]]+)\]\s*(.*)$/);
    if (match) {
      items.push({
        num: match[1],
        id: match[2],
        title: match[3].trim(),
      });
    } else if (
      items.length === 0 &&
      trimmed &&
      !trimmed.startsWith("Please reply")
    ) {
      header = header ? header + " " + trimmed : trimmed;
    }
  }

  return items.length > 0 ? { header, items } : null;
}

export default function CalvantCopilot() {
  const { isAuthenticated } = useSession();
  const effectiveOrgData = useEffectiveOrg();

  const pathname = usePathname();
  const isAdminPage = Boolean(pathname?.startsWith("/admin"));

  // Widget Open State (Fixed single-view docked panel)
  const [isOpen, setIsOpen] = useState(false);

  // Apple Intelligence iPhone 17 Pro Glowing Border State
  // Stays active continuously until human interaction (click, select, type, send, etc.)
  const [isGlowActive, setIsGlowActive] = useState(true);

  const turnOffGlow = useCallback(() => {
    setIsGlowActive(false);
  }, []);

  // ── Professional Integrated Document Workspace View State (No Popup Modal) ──
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewDocName, setPreviewDocName] = useState("");
  // isDashboardCollapsed: When document opens, dashboard collapses to slim left strip
  const [isDashboardCollapsed, setIsDashboardCollapsed] = useState(true);
  // isDocCollapsed: When user expands dashboard, document collapses to a slim strip next to copilot
  const [isDocCollapsed, setIsDocCollapsed] = useState(false);
  // docWidthPx: Width of document viewer when in resizable split mode
  const [docWidthPx, setDocWidthPx] = useState(520);
  const [splitPercent, setSplitPercent] = useState(50);
  const [isDraggingSplitter, setIsDraggingSplitter] = useState(false);
  const dragStartXRef = useRef(0);
  const dragStartWidthRef = useRef(520);

  // Helper to dynamically measure available workspace (between sidebar and copilot drawer)
  const getWorkspaceDimensions = useCallback(() => {
    if (typeof window === "undefined")
      return { availableWidth: 800, sidebarW: 70 };
    const sidebarEl = document.querySelector(".sidebar-wrapper, aside");
    const sidebarW = sidebarEl ? sidebarEl.getBoundingClientRect().width : 70;
    const copilotW = 405; // Copilot drawer width + margins
    const availableWidth = Math.max(
      480,
      window.innerWidth - sidebarW - copilotW,
    );
    return { availableWidth, sidebarW };
  }, []);

  const handleOpenDocPreview = (rawDocOrUrl, displayName = "") => {
    turnOffGlow(); // User interacted with document -> fade out glow!
    if (!rawDocOrUrl) return;
    const resolved = resolveDocUrl(rawDocOrUrl);
    setPreviewUrl(resolved);
    setPreviewDocName(
      displayName || getCleanDocName(String(rawDocOrUrl).split("/").pop()),
    );
    setPreviewModalOpen(true);
    const { availableWidth } = getWorkspaceDimensions();
    // Default split width is 50% of available workspace
    const initialSplitW = Math.round(availableWidth * 0.5);
    setDocWidthPx(initialSplitW);
    setSplitPercent(50);
    setIsDashboardCollapsed(true); // Document-First Focus initially
    setIsDocCollapsed(false);
  };

  const closePreviewModal = () => {
    setPreviewModalOpen(false);
    setPreviewUrl("");
    setPreviewDocName("");
    setIsDashboardCollapsed(false);
    setIsDocCollapsed(false);
  };

  const handleSplitterMouseDown = (e) => {
    e.preventDefault();
    setIsDraggingSplitter(true);
    dragStartXRef.current = e.clientX;
    dragStartWidthRef.current = docWidthPx;
  };

  useEffect(() => {
    if (!isDraggingSplitter) return;
    const handleMouseMove = (e) => {
      const { availableWidth } = getWorkspaceDimensions();
      const delta = dragStartXRef.current - e.clientX;
      const minDocW = Math.min(300, Math.floor(availableWidth * 0.25));
      const maxDocW = Math.max(320, availableWidth - 280);
      const newWidth = Math.max(
        minDocW,
        Math.min(maxDocW, dragStartWidthRef.current + delta),
      );
      setDocWidthPx(newWidth);
      setSplitPercent(Math.round((newWidth / availableWidth) * 100));
      // If dragging while collapsed, automatically un-collapse dashboard!
      if (isDashboardCollapsed) {
        setIsDashboardCollapsed(false);
      }
      if (isDocCollapsed) {
        setIsDocCollapsed(false);
      }
    };
    const handleMouseUp = () => {
      setIsDraggingSplitter(false);
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    isDraggingSplitter,
    getWorkspaceDimensions,
    isDashboardCollapsed,
    isDocCollapsed,
  ]);

  // Handle window resizing smoothly
  useEffect(() => {
    const handleResize = () => {
      const { availableWidth } = getWorkspaceDimensions();
      setDocWidthPx(Math.round(availableWidth * (splitPercent / 100)));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [getWorkspaceDimensions, splitPercent]);

  // Auto-close Document viewer when AI Copilot closes
  useEffect(() => {
    if (!isOpen && previewModalOpen) {
      closePreviewModal();
    }
  }, [isOpen, previewModalOpen]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const mc = document.querySelector(".main-content-wrapper, .main-content");

    const resetMc = () => {
      if (!mc) return;
      mc.style.removeProperty("display");
      mc.style.removeProperty("margin-right");
      mc.style.removeProperty("opacity");
    };

    if (!previewModalOpen || !isOpen) {
      document.body.classList.remove("doc-workspace-active");
      document.body.classList.remove("doc-split-active");
      resetMc();
      return;
    }

    document.body.classList.add("doc-workspace-active");
    if (!mc) return;

    // NOTE: copilot.css uses `margin-right: 400px !important` while the copilot is open,
    // so inline margins must also be !important or they get silently overridden.
    if (isDashboardCollapsed) {
      document.body.classList.remove("doc-split-active");
      // Dashboard is collapsed into the slim left strip — hide so it does NOT peek under PDF
      mc.style.setProperty("display", "none", "important");
      mc.style.removeProperty("margin-right");
    } else if (isDocCollapsed) {
      document.body.classList.remove("doc-split-active");
      // Document is collapsed into the slim right strip next to Copilot
      // (395px + 28px strip + 9px gap). Dashboard is fully expanded!
      mc.style.removeProperty("display");
      mc.style.setProperty("margin-right", "432px", "important");
    } else {
      // True Resizable Split view: Both Dashboard and Document are visible!
      document.body.classList.add("doc-split-active");
      mc.style.removeProperty("display");
      mc.style.setProperty(
        "margin-right",
        `${395 + docWidthPx + 16}px`,
        "important",
      );
    }

    return () => {
      document.body.classList.remove("doc-workspace-active");
      document.body.classList.remove("doc-split-active");
      resetMc();
    };
  }, [
    previewModalOpen,
    isOpen,
    isDashboardCollapsed,
    isDocCollapsed,
    docWidthPx,
  ]);

  // Chat conversation state
  const [messages, setMessages] = useState([]);
  const [expandedMsgIds, setExpandedMsgIds] = useState(new Set());
  const [inputValue, setInputValue] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [approvalResults, setApprovalResults] = useState({});

  // Toggle expand / collapse for previous messages
  const toggleMessageExpand = (msgId) => {
    turnOffGlow();
    setExpandedMsgIds((prev) => {
      const next = new Set(prev);
      if (next.has(msgId)) {
        next.delete(msgId);
      } else {
        next.add(msgId);
      }
      return next;
    });
  };

  // Generate single line / few words preview ending with ........
  const getMessagePreview = (text, maxLength = 45) => {
    if (!text) return "...";
    const plainText = text
      .replace(/#{1,6}\s+/g, "")
      .replace(/(\*\*|__)(.*?)\1/g, "$2")
      .replace(/(\*|_)(.*?)\1/g, "$2")
      .replace(/`([^`]+)`/g, "$1")
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      .replace(/<[^>]*>/g, "")
      .replace(/\s+/g, " ")
      .trim();

    if (plainText.length <= maxLength) {
      return plainText + " ........";
    }
    return plainText.slice(0, maxLength).trim() + " ........";
  };

  // Document Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadNotice, setUploadNotice] = useState(null);

  // CAL-69 Responsibilities Onboarding Modal State
  const [activeRespPrompt, setActiveRespPrompt] = useState(null);
  const [respItems, setRespItems] = useState([]);
  const [selectedRespIds, setSelectedRespIds] = useState([]);
  const [isAddingCustom, setIsAddingCustom] = useState(false);
  const [customInputText, setCustomInputText] = useState("");
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItemText, setEditingItemText] = useState("");
  const [isRespModalOpen, setIsRespModalOpen] = useState(false);

  const fileInputRef = useRef(null);
  const chatScrollRef = useRef(null);
  const textareaRef = useRef(null);

  // Live Deployed Backend Service URL (https://api.calvant.com/calvant-copilot-service)
  const COPILOT_API_BASE =
    (typeof process !== "undefined" &&
      process.env.NEXT_PUBLIC_COPILOT_API_URL) ||
    "https://api.calvant.com/calvant-copilot-service";

  // Derive identity from logged in user or session, ensuring 100% alignment with JWT token claims
  const user = effectiveOrgData?.user;
  let derivedUserId =
    user?._id ||
    user?.id ||
    user?.userId ||
    (typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem("email")
      : null) ||
    "user-1";

  let derivedOrgId =
    effectiveOrgData?.effectiveOrgId ||
    user?.organization?._id ||
    user?.organization ||
    "org-1";

  if (typeof window !== "undefined") {
    try {
      const rawToken =
        sessionStorage.getItem("token") || localStorage.getItem("token");
      if (rawToken && rawToken.includes(".")) {
        const payload = JSON.parse(
          atob(rawToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
        );
        if (payload?.sub) derivedUserId = payload.sub;
        if (payload?.organization) derivedOrgId = payload.organization;
      }
    } catch {
      // fallback
    }
  }

  const userId = derivedUserId;
  const orgId = derivedOrgId;

  const department =
    (Array.isArray(effectiveOrgData?.userDepartmentIds) &&
      effectiveOrgData.userDepartmentIds[0]) ||
    user?.department ||
    null;

  const rawRoles = effectiveOrgData?.userRoles ||
    user?.roles ||
    user?.role || ["RISK_OWNER"];
  const roles = Array.isArray(rawRoles) ? rawRoles : [rawRoles];
  const userRole = roles?.[0] || user?.role || "USER";

  // All organization departments list for ID -> Name lookup
  const [departmentsList, setDepartmentsList] = useState([]);

  useEffect(() => {
    let isMounted = true;
    getDepartments()
      .then((data) => {
        if (isMounted && Array.isArray(data)) {
          setDepartmentsList(data);
        }
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, []);

  // Derive human-readable department name to introduce in place of role in Copilot header
  const departmentName = useMemo(() => {
    // 1. Check user departments via departmentUtils
    const userDepts = getUserDepartments(user);
    const validNamedDepts = userDepts.filter(
      (d) =>
        d.name &&
        typeof d.name === "string" &&
        !/^[0-9a-fA-F]{24}$/.test(d.name.trim()),
    );
    if (validNamedDepts.length > 0) {
      return validNamedDepts.map((d) => d.name.trim()).join(", ");
    }

    // 2. Direct checks on user.department / user.departments object
    const rawDept = user?.department || user?.departments;
    if (rawDept) {
      if (
        typeof rawDept === "object" &&
        !Array.isArray(rawDept) &&
        rawDept.name
      ) {
        const n = String(rawDept.name).trim();
        if (n && !/^[0-9a-fA-F]{24}$/.test(n)) return n;
      }
      if (Array.isArray(rawDept)) {
        const names = rawDept
          .map((d) =>
            typeof d === "object" ? d?.name : typeof d === "string" ? d : null,
          )
          .filter(
            (n) =>
              n && typeof n === "string" && !/^[0-9a-fA-F]{24}$/.test(n.trim()),
          );
        if (names.length > 0) return names.join(", ");
      }
      if (
        typeof rawDept === "string" &&
        !/^[0-9a-fA-F]{24}$/.test(rawDept.trim())
      ) {
        return rawDept.trim();
      }
    }

    // 3. Check sessionStorage user object
    if (typeof window !== "undefined") {
      try {
        const stored = sessionStorage.getItem("user");
        if (stored) {
          const su = JSON.parse(stored);
          if (
            su.department?.name &&
            !/^[0-9a-fA-F]{24}$/.test(su.department.name)
          ) {
            return String(su.department.name).trim();
          }
          if (Array.isArray(su.departments)) {
            const sn = su.departments
              .map((d) => (typeof d === "object" ? d?.name : d))
              .filter(
                (n) =>
                  n &&
                  typeof n === "string" &&
                  !/^[0-9a-fA-F]{24}$/.test(n.trim()),
              );
            if (sn.length > 0) return sn.join(", ");
          }
          if (
            typeof su.department === "string" &&
            !/^[0-9a-fA-F]{24}$/.test(su.department.trim())
          ) {
            return su.department.trim();
          }
        }
      } catch (e) {}
    }

    // 4. Map department ID to human-readable name from departmentsList
    const targetDeptIds = [];
    if (Array.isArray(effectiveOrgData?.userDepartmentIds)) {
      targetDeptIds.push(...effectiveOrgData.userDepartmentIds);
    }
    userDepts.forEach((d) => {
      if (d._id) targetDeptIds.push(d._id);
      if (d.id) targetDeptIds.push(d.id);
    });
    if (user?.department && typeof user.department === "string") {
      targetDeptIds.push(user.department);
    }

    if (targetDeptIds.length > 0 && departmentsList.length > 0) {
      const matched = departmentsList.filter((d) =>
        targetDeptIds.some(
          (tid) =>
            String(tid).toLowerCase() ===
            String(d._id || d.id).toLowerCase(),
        ),
      );
      if (matched.length > 0) {
        return matched
          .map((d) => d.name)
          .filter(Boolean)
          .join(", ");
      }
    }

    return "";
  }, [user, departmentsList, effectiveOrgData?.userDepartmentIds]);

  // Derive current logged-in user's display name
  const userName = (() => {
    if (user?.name) return user.name;
    if (user?.fullName) return user.fullName;
    if (user?.firstName) {
      return `${user.firstName} ${user.lastName || ""}`.trim();
    }
    if (user?.username) return user.username;
    if (typeof window !== "undefined") {
      try {
        const sUser = sessionStorage.getItem("user");
        if (sUser) {
          const parsed = JSON.parse(sUser);
          if (parsed?.name) return parsed.name;
          if (parsed?.fullName) return parsed.fullName;
          if (parsed?.username) return parsed.username;
          if (parsed?.firstName)
            return `${parsed.firstName} ${parsed.lastName || ""}`.trim();
        }
        const sName =
          sessionStorage.getItem("name") ||
          sessionStorage.getItem("uname") ||
          sessionStorage.getItem("username");
        if (sName) return sName;
        const sEmail = sessionStorage.getItem("email");
        if (sEmail && sEmail.includes("@")) {
          return sEmail.split("@")[0];
        }
      } catch {}
    }
    if (user?.email && user.email.includes("@")) {
      return user.email.split("@")[0];
    }
    return "root";
  })();

  // Track the previous userId so we can detect account switches (logout → login as different user)
  const prevUserIdRef = useRef(userId);

  // Auth headers generator with JWT token
  const getAuthHeaders = useCallback(
    (extraHeaders = {}) => {
      let token =
        typeof sessionStorage !== "undefined"
          ? sessionStorage.getItem("token") || localStorage.getItem("token")
          : "";

      // Ensure the token has the sub and organization claims required by calvant-copilot-service
      let isValidCopilotToken = false;
      if (token) {
        try {
          const parts = token.split(".");
          if (parts.length >= 2) {
            const payload = JSON.parse(
              atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
            );
            if (payload && payload.sub && payload.organization) {
              isValidCopilotToken = true;
            }
          }
        } catch {
          isValidCopilotToken = false;
        }
      }

      if (!isValidCopilotToken) {
        token = generateMockJwt(userId, orgId, department, roles);
      }

      return {
        Authorization: `Bearer ${token}`,
        ...extraHeaders,
      };
    },
    [userId, orgId, department, roles],
  );

  const generateNewSessionId = () =>
    "sess-" + Math.random().toString(36).slice(2, 10);

  // ── Auto-reset chat when user identity changes (account switch / refresh / logout+login) ──
  useEffect(() => {
    if (prevUserIdRef.current && prevUserIdRef.current !== userId) {
      // User changed: wipe entire chat state for a clean start
      setIsOpen(false);
      setMessages([]);
      setExpandedMsgIds(new Set());
      setInputValue("");
      setSessionId("");
      setApprovalResults({});
      setActiveRespPrompt(null);
      setRespItems([]);
      setSelectedRespIds([]);
      setIsAddingCustom(false);
      setCustomInputText("");
      setEditingItemId(null);
      setEditingItemText("");
      setIsRespModalOpen(false);
      setIsBusy(false);
      setIsConnecting(false);
      if (textareaRef.current) textareaRef.current.style.height = "auto";
      // Also remove the body class so page content un-shifts
      if (typeof document !== "undefined") {
        document.body.classList.remove("copilot-sidebar-open");
      }
    }
    prevUserIdRef.current = userId;
  }, [userId]);

  // ── Auto-reset chat when user logs out (isAuthenticated becomes false) ──
  useEffect(() => {
    if (isAuthenticated === false) {
      setIsOpen(false);
      setMessages([]);
      setExpandedMsgIds(new Set());
      setInputValue("");
      setSessionId("");
      setApprovalResults({});
      setActiveRespPrompt(null);
      setRespItems([]);
      setSelectedRespIds([]);
      setIsAddingCustom(false);
      setCustomInputText("");
      setEditingItemId(null);
      setEditingItemText("");
      setIsRespModalOpen(false);
      setIsBusy(false);
      setIsConnecting(false);
      if (typeof document !== "undefined") {
        document.body.classList.remove("copilot-sidebar-open");
      }
    }
  }, [isAuthenticated]);

  // Persistent Sidebar Content Shift: add copilot-sidebar-open class on body so page content smoothly shifts left
  useEffect(() => {
    if (typeof document === "undefined") return;

    if (isOpen) {
      document.body.classList.add("copilot-sidebar-open");
    } else {
      document.body.classList.remove("copilot-sidebar-open");
    }

    return () => {
      document.body.classList.remove("copilot-sidebar-open");
    };
  }, [isOpen]);

  // Connect to live backend on open: fetch real session greeting via POST /api/copilot/session/start
  const startSession = useCallback(
    async (currentSessId) => {
      const activeSession =
        currentSessId || sessionId || generateNewSessionId();
      setSessionId(activeSession);

      setIsConnecting(true);
      try {
        const res = await fetch(
          `${COPILOT_API_BASE}/api/copilot/session/start`,
          {
            method: "POST",
            headers: getAuthHeaders({ "Content-Type": "application/json" }),
            body: JSON.stringify({
              orgId: String(orgId),
              sessionId: activeSession,
              userId: String(userId),
              roles: Array.isArray(roles) ? roles : [roles],
            }),
          },
        );

        if (res.ok) {
          const data = await res.json();
          setMessages([
            {
              id: "greet-" + Date.now(),
              role: "assistant",
              reply: data.reply,
              disclaimer: data.disclaimer,
              intent: data.activeIntent || null,
              detectedDomains: data.detectedDomains,
              timestamp: new Date(),
            },
          ]);
        } else {
          const errData = await res.json().catch(() => null);
          setMessages([
            {
              id: "greet-err-" + Date.now(),
              role: "assistant",
              isError: true,
              reply:
                errData?.error ||
                errData?.message ||
                `Error starting session (HTTP ${res.status}). Verify user authorization and tenant access.`,
              disclaimer: errData?.disclaimer,
              timestamp: new Date(),
            },
          ]);
        }
      } catch (err) {
        setMessages([
          {
            id: "conn-err-" + Date.now(),
            role: "assistant",
            isError: true,
            reply: `Unable to connect to live copilot service at \`${COPILOT_API_BASE}\`. Error: ${String(err)}`,
            timestamp: new Date(),
          },
        ]);
      } finally {
        setIsConnecting(false);
      }
    },
    [COPILOT_API_BASE, getAuthHeaders, orgId, sessionId, userId, roles],
  );

  // Handle widget toggle
  const handleToggleWidget = () => {
    if (!isOpen) {
      setIsOpen(true);
      setIsGlowActive(true); // Fresh open: neon border glow starts active!
      if (messages.length === 0) {
        const newId = generateNewSessionId();
        setSessionId(newId);
        startSession(newId);
      }
    } else {
      setIsOpen(false);
      closePreviewModal();
    }
  };

  const handleResetSession = () => {
    setIsGlowActive(true);
    const newId = generateNewSessionId();
    setSessionId(newId);
    setMessages([]);
    setExpandedMsgIds(new Set());
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
    setApprovalResults({});
    setActiveRespPrompt(null);
    setRespItems([]);
    setSelectedRespIds([]);
    setIsAddingCustom(false);
    setCustomInputText("");
    setEditingItemId(null);
    setEditingItemText("");
    setIsRespModalOpen(false);
    startSession(newId);
  };

  // Detect first-time responsibilities prompt from assistant and auto-open modal
  useEffect(() => {
    if (messages.length === 0) {
      setActiveRespPrompt(null);
      setIsRespModalOpen(false);
      return;
    }
    const lastMsg = messages[messages.length - 1];
    if (lastMsg && lastMsg.role === "assistant") {
      const parsed = parseResponsibilitiesPrompt(lastMsg.reply);
      if (parsed) {
        setActiveRespPrompt(parsed);
        setRespItems(parsed.items || []);
        setIsRespModalOpen(true);
        setSelectedRespIds([]);
        setIsAddingCustom(false);
        setCustomInputText("");
        setEditingItemId(null);
        setEditingItemText("");
        return;
      }
      // Assistant replied with normal message - setup complete
      setActiveRespPrompt(null);
      setIsRespModalOpen(false);
    }
  }, [messages]);

  // Toggle selection for any responsibility item
  const toggleRespItem = (id) => {
    turnOffGlow();
    setSelectedRespIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  // Quick select/clear all responsibility items in current list
  const handleSelectAllResp = () => {
    turnOffGlow();
    if (selectedRespIds.length === respItems.length) {
      setSelectedRespIds([]);
    } else {
      setSelectedRespIds(respItems.map((i) => i.id));
    }
  };

  // Add a manual custom responsibility item to the list above (selected by default)
  const handleAddCustomItem = () => {
    turnOffGlow();
    const text = customInputText.trim();
    if (!text) return;
    const customCount = respItems.filter((i) => i.isCustom).length + 1;
    const newId = `RESP-CUSTOM-${String(customCount).padStart(2, "0")}`;
    const newItem = {
      id: newId,
      title: text,
      isCustom: true,
    };
    setRespItems((prev) => [...prev, newItem]);
    // By default select the newly added item
    setSelectedRespIds((prev) => [...prev, newId]);
    setCustomInputText("");
    setIsAddingCustom(false);
  };

  // Inline editing handlers
  const handleStartEdit = (e, item) => {
    e.stopPropagation();
    setEditingItemId(item.id);
    setEditingItemText(item.title);
  };

  const handleSaveEdit = (e, id) => {
    e.stopPropagation();
    const trimmed = editingItemText.trim();
    if (trimmed) {
      setRespItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, title: trimmed } : i)),
      );
    }
    setEditingItemId(null);
    setEditingItemText("");
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setEditingItemId(null);
    setEditingItemText("");
  };

  // Submit responsibilities to backend orchestrator and launch fresh greeted session
  const handleSendResponsibilities = async () => {
    if (isAddingCustom || editingItemId !== null) return;

    const selectedItems = respItems.filter((item) =>
      selectedRespIds.includes(item.id),
    );

    if (selectedItems.length === 0) return;

    const formatted = selectedItems
      .map((item) => `[${item.id}] ${item.title}`)
      .join(", ");

    setIsRespModalOpen(false);
    setIsBusy(true);

    try {
      const activeSession = sessionId || generateNewSessionId();
      // Send responsibilities message to backend orchestrator so it persists the selections
      await fetch(`${COPILOT_API_BASE}/api/copilot/chat`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          message: formatted,
          orgId: String(orgId),
          sessionId: activeSession,
          userId: String(userId),
          department: department ? String(department) : null,
          roles: Array.isArray(roles) ? roles : [roles],
        }),
      });
    } catch (err) {
      console.error("Error submitting responsibilities:", err);
    } finally {
      // Clear modal & prompt states cleanly
      setActiveRespPrompt(null);
      setRespItems([]);
      setSelectedRespIds([]);
      setIsAddingCustom(false);
      setCustomInputText("");
      setEditingItemId(null);
      setEditingItemText("");
      setApprovalResults({});
      setMessages([]);

      // Always launch a new chat session where by default via APIs it greets the user
      const newSessId = generateNewSessionId();
      setSessionId(newSessId);
      setIsBusy(false);
      await startSession(newSessId);
    }
  };

  // Send message to real backend (POST /api/copilot/chat)
  const handleSendMessage = async (customMessage) => {
    turnOffGlow();
    const textToSend = (customMessage || inputValue).trim();
    if (!textToSend || isBusy) return;

    const userMsgId = "user-" + Date.now();
    const newMsgList = [
      ...messages,
      {
        id: userMsgId,
        role: "user",
        reply: textToSend,
        timestamp: new Date(),
      },
    ];

    setMessages(newMsgList);
    setInputValue("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "36px";
      textareaRef.current.style.overflowY = "hidden";
    }
    setIsBusy(true);

    const activeSession = sessionId || generateNewSessionId();
    if (!sessionId) setSessionId(activeSession);

    const bodyPayload = {
      message: textToSend,
      orgId: String(orgId),
      sessionId: activeSession,
      userId: String(userId),
      department: department ? String(department) : null,
      roles: Array.isArray(roles) ? roles : [roles],
    };

    try {
      const res = await fetch(`${COPILOT_API_BASE}/api/copilot/chat`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(bodyPayload),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        setMessages((prev) => [
          ...prev,
          {
            id: "err-" + Date.now(),
            role: "assistant",
            isError: true,
            reply:
              data?.error ||
              data?.message ||
              `Unable to complete request (HTTP ${res.status}). Verify your authorization or department assignments.`,
            disclaimer: data?.disclaimer,
            timestamp: new Date(),
          },
        ]);
      } else {
        // Check for DocGen Artifact ID for Approval State Machine (CAL-68)
        const replyText = data.reply || "";
        const newArtifactMatch = replyText.match(
          /Draft ready - pending ROOT approval \(ID:\s*([A-Za-z0-9_-]+)\)/i,
        );
        const legacyArtifactMatch = replyText.match(
          /\[Status:\s*Pending Human Approval\s*-\s*Artifact ID:\s*([A-Za-z0-9_-]+)\]/i,
        );
        const outputId = newArtifactMatch
          ? newArtifactMatch[1]
          : legacyArtifactMatch
            ? legacyArtifactMatch[1]
            : data.generatedOutputId || data.outputId || null;

        setMessages((prev) => [
          ...prev,
          {
            id: "ai-" + Date.now(),
            role: "assistant",
            reply: data.reply,
            intent: data.activeIntent,
            detectedDomains: data.detectedDomains,
            disclaimer: data.disclaimer,
            outputId: outputId,
            timestamp: new Date(),
          },
        ]);
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: "err-network-" + Date.now(),
          role: "assistant",
          isError: true,
          reply: `Network connection error calling \`${COPILOT_API_BASE}/api/copilot/chat\`: ${String(err)}`,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsBusy(false);
      setTimeout(() => {
        if (textareaRef.current) textareaRef.current.focus();
      }, 50);
    }
  };

  // Handle dynamic auto-growing input textarea up to 140px
  const handleInputChange = (e) => {
    turnOffGlow();
    const val = e.target.value;
    setInputValue(val);

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const scrollH = textareaRef.current.scrollHeight;
      const nextHeight = Math.min(Math.max(scrollH, 36), 140);
      textareaRef.current.style.height = `${nextHeight}px`;
      textareaRef.current.style.overflowY = scrollH > 140 ? "auto" : "hidden";
    }
  };

  // Handle Enter key submit
  const handleKeyDown = (e) => {
    turnOffGlow();
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ── CAL-68 DocGen Approvals & Downloads ───────────────────────────────────
  const handleApproveArtifact = async (outputId) => {
    turnOffGlow();
    setApprovalResults((prev) => ({
      ...prev,
      [outputId]: { loading: true, message: "Approving draft…" },
    }));

    try {
      const res = await fetch(
        `${COPILOT_API_BASE}/api/copilot/outputs/${encodeURIComponent(outputId)}/approve`,
        {
          method: "POST",
          headers: getAuthHeaders({ "Content-Type": "application/json" }),
        },
      );
      const data = await res.json().catch(() => null);
      if (res.ok) {
        setApprovalResults((prev) => ({
          ...prev,
          [outputId]: {
            success: true,
            message: `✓ Approved by ${data?.output?.approvedBy || "Authorized Reviewer"} (${data?.status || "APPROVED"})`,
          },
        }));

        // Persist to local outputs cache so ROOT user sees it in AI Approval page under Approved!
        try {
          const key = `calvant_outputs_${orgId}`;
          const raw = localStorage.getItem(key);
          const list = raw ? JSON.parse(raw) : [];
          const approvedItem = data?.output || {
            id: outputId,
            status: "APPROVED",
            userId: userId,
            approvedBy: userId || "ROOT",
            approvedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          const updated = [
            approvedItem,
            ...list.filter((o) => o.id !== outputId),
          ];
          localStorage.setItem(key, JSON.stringify(updated));
        } catch (_) {}
      } else {
        setApprovalResults((prev) => ({
          ...prev,
          [outputId]: {
            error: true,
            message: `Approval failed: ${data?.error || res.statusText}`,
          },
        }));
      }
    } catch (err) {
      setApprovalResults((prev) => ({
        ...prev,
        [outputId]: { error: true, message: `Error: ${String(err)}` },
      }));
    }
  };

  const handleRejectArtifact = async (outputId) => {
    turnOffGlow();
    const reason = prompt(
      "Please enter rejection reason:",
      "Rejected by reviewer during policy audit",
    );
    if (reason === null) return;

    setApprovalResults((prev) => ({
      ...prev,
      [outputId]: { loading: true, message: "Rejecting draft…" },
    }));

    try {
      const res = await fetch(
        `${COPILOT_API_BASE}/api/copilot/outputs/${encodeURIComponent(outputId)}/reject`,
        {
          method: "POST",
          headers: getAuthHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ reason }),
        },
      );
      const data = await res.json().catch(() => null);
      if (res.ok) {
        setApprovalResults((prev) => ({
          ...prev,
          [outputId]: {
            error: true,
            message: `✕ Rejected: ${data?.output?.rejectionReason || reason}`,
          },
        }));
      } else {
        setApprovalResults((prev) => ({
          ...prev,
          [outputId]: {
            error: true,
            message: `Rejection failed: ${data?.error || res.statusText}`,
          },
        }));
      }
    } catch (err) {
      setApprovalResults((prev) => ({
        ...prev,
        [outputId]: { error: true, message: `Error: ${String(err)}` },
      }));
    }
  };

  const handleDownloadMarkdown = async (outputId) => {
    turnOffGlow();
    try {
      const res = await fetch(
        `${COPILOT_API_BASE}/api/copilot/outputs/${encodeURIComponent(outputId)}/download`,
        {
          method: "GET",
          headers: getAuthHeaders(),
        },
      );
      if (res.ok) {
        const text = await res.text();
        const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `${outputId}.md`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      } else {
        const data = await res.json().catch(() => null);
        alert(
          `Download blocked (HTTP ${res.status}): ${data?.error || "Requires ROOT approval before download"}`,
        );
      }
    } catch (err) {
      alert("Download error: " + err);
    }
  };

  const handleExportDoc = async (outputId) => {
    turnOffGlow();
    try {
      const res = await fetch(
        `${COPILOT_API_BASE}/api/copilot/outputs/${encodeURIComponent(outputId)}/export`,
        {
          method: "POST",
          headers: getAuthHeaders({ "Content-Type": "application/json" }),
        },
      );
      const data = await res.json().catch(() => null);
      if (res.ok && data?.output) {
        const fullContent =
          (data.output.content || "") +
          (data.disclaimer ? `\n\n---\n*Disclaimer: ${data.disclaimer}*` : "");
        const htmlDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(
          data.output.id,
        )}</title><style>body{font-family:'Segoe UI',Calibri,Arial,sans-serif;font-size:11pt;line-height:1.6;color:#1e293b;margin:40px;}h1,h2,h3{color:#4338ca;}hr{border:none;border-top:1px solid #cbd5e1;margin:20px 0;}.disclaimer{font-size:9.5pt;color:#64748b;font-style:italic;margin-top:24px;border-top:1px solid #e2e8f0;padding-top:12px;}</style></head><body>${formatMarkdownToHtml(
          fullContent,
        )}</body></html>`;

        const blob = new Blob(["\ufeff", htmlDoc], {
          type: "application/msword;charset=utf-8",
        });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `${outputId}.doc`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      } else {
        alert(
          `Export blocked: ${data?.error || "Requires approval before exporting"}`,
        );
      }
    } catch (err) {
      alert("Export error: " + err);
    }
  };

  // ── Document Ingestion (RAG Upload) ──────────────────────────────────────
  const handleFileUpload = async (e) => {
    turnOffGlow();
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadNotice("Uploading and ingesting document…");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("organizationId", String(orgId));
    if (department) formData.append("department", String(department));

    try {
      const res = await fetch(
        `${COPILOT_API_BASE}/api/copilot/documents/upload`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: formData,
        },
      );
      const data = await res.json().catch(() => null);
      if (res.ok) {
        setUploadNotice(
          `✓ Ingested "${data?.file || file.name}" (${data?.chunksStored || 0} chunks stored)`,
        );
        setMessages((prev) => [
          ...prev,
          {
            id: "upload-" + Date.now(),
            role: "assistant",
            reply: `📄 Document **${file.name}** has been ingested successfully into RAG storage! You can now query its contents.`,
            intent: "DOC_SUMMARY",
            timestamp: new Date(),
          },
        ]);
      } else {
        setUploadNotice(
          `✕ Upload failed (HTTP ${res.status}): ${data?.error || "Error indexing document"}`,
        );
      }
    } catch (err) {
      setUploadNotice(`✕ Upload network error: ${String(err)}`);
    } finally {
      setIsUploading(false);
      setTimeout(() => setUploadNotice(null), 5000);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ── AUTH GATE: Hide Copilot entirely on logged-out pages ──
  // isAuthenticated is null during SSR hydration check, false when logged out, true when logged in
  if (!isAuthenticated) return null;

  return (
    <>
      {/* ── 1. FLOATING LAUNCHER BUTTON (Matches CalVant Theme: AI Copilot with small star) ── */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center">
          <button
            onClick={handleToggleWidget}
            aria-label="Open AI Copilot"
            className="copilot-fab-glow group relative flex items-center gap-2 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-full bg-gradient-to-r from-[#667eea] to-[#764ba2] shadow-[0_4px_18px_rgba(102,126,234,0.45)] hover:shadow-[0_8px_28px_rgba(118,75,162,0.6)] text-white font-semibold text-xs sm:text-[13px] tracking-wide transition-all duration-300 hover:scale-105 active:scale-95 focus:outline-none focus:ring-4 focus:ring-[#667eea]/30 select-none"
          >
            {/* Ambient Pulse Glow */}
            <div className="absolute inset-0 rounded-full bg-[#667eea]/30 blur-md -z-10 group-hover:bg-[#667eea]/50 transition-colors" />

            {/* Unified Logo Icon with Small Star */}
            <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-lg bg-white/20 backdrop-blur-xs flex items-center justify-center flex-shrink-0 border border-white/30 shadow-2xs">
              <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
            </div>

            <span className="font-semibold text-white tracking-wide">
              AI Copilot
            </span>

            {/* Online Green Status Dot */}
            <span className="w-2 h-2 bg-emerald-400 border border-white rounded-full shadow-sm ml-0.5" />
          </button>
        </div>
      )}

      {/* ── 2. IPHONE 17 PRO CURVY FLOATING COPILOT WITH 6-COLOR NEON GLOW ── */}
      {isOpen && (
        <div
          onClickCapture={turnOffGlow}
          onPointerDownCapture={turnOffGlow}
          onFocusCapture={turnOffGlow}
          onKeyDownCapture={turnOffGlow}
          onSelectCapture={turnOffGlow}
          onChangeCapture={turnOffGlow}
          className={`copilot-iphone-wrapper fixed right-3 z-[99999] flex flex-col w-[calc(100vw-24px)] sm:w-[380px] ${
            isAdminPage
              ? "top-3 bottom-3 h-[calc(100vh-24px)]"
              : "top-[84px] sm:top-[88px] bottom-3.5 h-[calc(100vh-98px)] sm:h-[calc(100vh-104px)]"
          }`}
          style={{ zIndex: 99999 }}
          role="complementary"
          aria-label="AI Copilot"
        >
          {/* Holi Color Splash / Siri Chromatic Bloom Cloud */}
          <div
            className={`copilot-chromatic-splash ${
              isGlowActive ? "copilot-glow-on" : "copilot-glow-off"
            }`}
            aria-hidden="true"
          />

          {/* Apple Intelligence 6-Color Neon Glow - Ambient Outer Aura */}
          <div
            className={`copilot-glow-ambient ${
              isGlowActive ? "copilot-glow-on" : "copilot-glow-off"
            }`}
            aria-hidden="true"
          >
            <div className="copilot-neon-rotator" />
          </div>

          {/* Apple Intelligence 6-Color Neon Glow - Crisp Curved Perimeter Ribbon */}
          <div
            className={`copilot-glow-perimeter ${
              isGlowActive ? "copilot-glow-on" : "copilot-glow-off"
            }`}
            aria-hidden="true"
          >
            <div className="copilot-neon-rotator" />
          </div>

          {/* Inner Curvy iPhone Card */}
          <div className="copilot-iphone-card relative z-10 w-full h-full flex flex-col overflow-hidden bg-white shadow-2xl">
            {/* ── Header (Exact "Start Tutorial" Gradient: #667eea to #764ba2) ── */}
            <div className="flex items-center justify-between px-3.5 sm:px-4 py-2.5 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white shadow-sm select-none flex-shrink-0">
              <div className="flex items-center gap-2.5 min-w-0 flex-1 mr-2">
                {/* Unified Star/Sparkles Icon Box */}
                <div className="w-7 h-7 rounded-lg bg-white/20 backdrop-blur-md border border-white/30 shadow-xs flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-xs sm:text-[13px] font-semibold tracking-wide text-white truncate flex items-center gap-1.5">
                      <span className="inline-flex items-center">
                        calvant
                        <span className="w-1.5 h-1.5 rounded-full bg-white ml-0.5 -mt-0.5 inline-block" />
                      </span>
                      <span>AI CoPilot</span>
                    </h3>
                    <span className="px-1.5 py-0.2 text-[8.5px] font-semibold tracking-wider bg-white/20 text-white rounded-full border border-white/30 flex-shrink-0">
                      LIVE
                    </span>
                  </div>
                  <div
                    className="mt-0.5 text-[10.5px] text-white/90 truncate cursor-help select-none"
                    title={`${userName} · ${userRole}${departmentName ? ` · ${departmentName}` : ""}`}
                  >
                    <span className="text-white font-medium">{userName}</span>
                    <span className="text-white/40 mx-1">·</span>
                    <span className="text-white/80 font-medium">{userRole}</span>
                    {departmentName && (
                      <>
                        <span className="text-white/40 mx-1">·</span>
                        <span className="text-white/90 font-medium">{departmentName}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Header Controls */}
              <div className="flex items-center gap-1 text-white/90 flex-shrink-0">
                <button
                  onClick={handleResetSession}
                  title="New Chat Session"
                  className="p-1.5 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>

                <button
                  onClick={handleToggleWidget}
                  title="Close Copilot"
                  className="p-1.5 hover:text-white hover:bg-rose-500/40 rounded-lg transition-colors ml-1"
                >
                  <X className="w-4.5 h-4.5 text-white" />
                </button>
              </div>
            </div>

            {/* ── Notice Strip (e.g. Upload feedback) ── */}
            {uploadNotice && (
              <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-100 text-indigo-900 text-xs font-mono flex items-center justify-between">
                <span>{uploadNotice}</span>
                <button
                  onClick={() => setUploadNotice(null)}
                  className="text-indigo-400 hover:text-indigo-700"
                >
                  ✕
                </button>
              </div>
            )}

            {/* ── Messages Stream ── */}
            <div
              ref={chatScrollRef}
              className="copilot-scroll flex-1 p-5 overflow-y-auto space-y-4 bg-slate-50/50"
            >
              {isConnecting && messages.length === 0 && (
                <div className="flex items-center justify-center py-16 gap-3 text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin text-[#667eea]" />
                  <span className="text-sm font-medium">
                    Connecting to CalVant AI CoPilot…
                  </span>
                </div>
              )}

              {messages.map((msg, index) => {
                const isLatest = index === messages.length - 1;
                const isExpandedByUser = expandedMsgIds.has(msg.id);
                // Previous messages collapse into a single line with '........' unless expanded by user
                const isCollapsed = !isLatest && !isExpandedByUser;

                return (
                  <div
                    key={msg.id}
                    className={`copilot-msg-enter flex flex-col ${
                      msg.role === "user" ? "items-end" : "items-start"
                    }`}
                  >
                    {/* Collapsed single-line view for previous messages */}
                    {isCollapsed ? (
                      msg.role === "user" ? (
                        /* Collapsed User Message */
                        <button
                          type="button"
                          onClick={() => toggleMessageExpand(msg.id)}
                          title="Click to expand message"
                          className="cursor-pointer group flex items-center gap-2 max-w-[85%] bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white px-3.5 py-1.5 rounded-full shadow-sm text-xs font-medium hover:opacity-95 active:scale-95 transition-all text-left"
                        >
                          <span className="truncate">
                            {getMessagePreview(msg.reply, 38)}
                          </span>
                          <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 text-white/80 group-hover:translate-y-0.5 transition-transform" />
                        </button>
                      ) : (
                        /* Collapsed Assistant Message */
                        <button
                          type="button"
                          onClick={() => toggleMessageExpand(msg.id)}
                          title="Click to expand full response"
                          className="cursor-pointer group flex items-center justify-between gap-2.5 max-w-[95%] bg-white border border-slate-200/90 hover:border-[#667eea]/50 rounded-xl px-3 py-2 shadow-sm text-xs text-slate-700 hover:shadow-md active:scale-[0.99] transition-all text-left"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-5 h-5 rounded-md bg-[#667eea]/10 flex items-center justify-center flex-shrink-0 text-[#667eea]">
                              <Sparkles className="w-3 h-3" />
                            </div>
                            <span className="truncate font-medium text-slate-700">
                              {getMessagePreview(msg.reply, 40)}
                            </span>
                          </div>
                          <span className="flex items-center gap-1 text-[11px] font-semibold text-[#667eea] flex-shrink-0 ml-1">
                            <span>Expand</span>
                            <ChevronDown className="w-3 h-3 group-hover:translate-y-0.5 transition-transform" />
                          </span>
                        </button>
                      )
                    ) : /* Expanded Message */
                    msg.role === "user" ? (
                      /* User Message (Exact CalVant Brand Gradient, compact font) */
                      <div
                        onClick={
                          !isLatest
                            ? () => toggleMessageExpand(msg.id)
                            : undefined
                        }
                        className={`max-w-[85%] bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white px-3 py-1.5 rounded-2xl rounded-tr-sm shadow-[0_2px_10px_rgba(102,126,234,0.25)] text-[11.5px] leading-relaxed break-words font-medium transition-all ${
                          !isLatest ? "cursor-pointer hover:opacity-95" : ""
                        }`}
                        title={
                          !isLatest ? "Click to collapse message" : undefined
                        }
                      >
                        <div>{msg.reply}</div>
                        {!isLatest && (
                          <div className="flex justify-end mt-1 pt-0.5 border-t border-white/20">
                            <span className="text-[9px] text-white/80 flex items-center gap-1">
                              Collapse <ChevronUp className="w-2.5 h-2.5" />
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Copilot Assistant Message (Expanded, compact font) */
                      <div className="w-full flex items-start gap-2">
                        {/* Branded AI Avatar */}
                        <div className="w-5 h-5 rounded-md bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white flex items-center justify-center flex-shrink-0 shadow-xs mt-0.5">
                          <Sparkles className="w-2.5 h-2.5" />
                        </div>

                        <div className="flex-1 min-w-0">
                          {/* Assistant Header & Collapse Toggle */}
                          <div className="flex items-center justify-between mb-1 px-0.5">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[11px] font-semibold text-slate-800">
                                AI CoPilot
                              </span>
                              <span className="text-[8px] font-bold px-1.5 py-0.2 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100">
                                AI
                              </span>
                            </div>

                            {!isLatest && (
                              <button
                                type="button"
                                onClick={() => toggleMessageExpand(msg.id)}
                                className="flex items-center gap-1 text-[9.5px] text-slate-400 hover:text-indigo-600 transition-colors"
                                title="Collapse message"
                              >
                                <span>Collapse</span>
                                <ChevronUp className="w-2.5 h-2.5" />
                              </button>
                            )}
                          </div>

                          {/* Message Body Card (Compact font) */}
                          <div className="bg-white border border-slate-200/90 rounded-2xl rounded-tl-sm p-2.5 shadow-xs text-slate-800 text-[11.5px] relative group max-w-full overflow-hidden break-words">
                            {/* Live Intent & Domain Badges */}
                            {msg.intent && (
                              <div className="flex items-center gap-2 mb-1.5">
                                <span
                                  className={`text-[8.5px] font-mono font-semibold px-1.5 py-0.2 rounded-full border ${
                                    msg.intent === "DOCGEN"
                                      ? "bg-purple-50 text-purple-700 border-purple-200"
                                      : msg.intent === "COMPLIANCE"
                                        ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                                        : msg.intent === "TOOL_GUIDE"
                                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                          : "bg-amber-50 text-amber-700 border-amber-200"
                                  }`}
                                >
                                  {msg.intent}
                                </span>
                                {msg.detectedDomains &&
                                  msg.detectedDomains.length > 0 && (
                                    <span className="text-[8.5px] text-slate-500 font-mono">
                                      Domains: [{msg.detectedDomains.join(", ")}
                                      ]
                                    </span>
                                  )}
                              </div>
                            )}

                            {/* Responsibilities Prompt Interactive Card or Standard Markdown */}
                            {parseResponsibilitiesPrompt(msg.reply) ? (
                              <div className="space-y-2.5">
                                <div className="flex items-center gap-2 text-indigo-900 font-semibold text-xs">
                                  <div className="w-5 h-5 rounded-md bg-indigo-50 border border-indigo-200 flex items-center justify-center text-[#667eea]">
                                    <Sparkles className="w-3 h-3" />
                                  </div>
                                  <span>
                                    {parseResponsibilitiesPrompt(msg.reply)
                                      .header || "Role Responsibilities Setup"}
                                  </span>
                                </div>

                                <p className="text-[11px] text-slate-600 leading-relaxed">
                                  Please configure your role responsibilities
                                  using the interactive selector to personalize
                                  your Copilot compliance tools.
                                </p>

                                <div className="pt-0.5">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      turnOffGlow();
                                      setActiveRespPrompt(
                                        parseResponsibilitiesPrompt(msg.reply),
                                      );
                                      setIsRespModalOpen(true);
                                    }}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white text-[11px] font-semibold shadow-[0_2px_8px_rgba(102,126,234,0.3)] hover:opacity-95 active:scale-95 transition-all"
                                  >
                                    <Sparkles className="w-3 h-3" />
                                    <span>
                                      Open Responsibilities Selector (
                                      {
                                        parseResponsibilitiesPrompt(msg.reply)
                                          .items.length
                                      }{" "}
                                      Options)
                                    </span>
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div
                                  className="copilot-md leading-relaxed text-slate-700 text-[11.5px]"
                                  onClick={(e) => {
                                    const chip =
                                      e.target.closest("[data-doc-url]");
                                    if (chip) {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const url =
                                        chip.getAttribute("data-doc-url");
                                      const name =
                                        chip.getAttribute("data-doc-name");
                                      handleOpenDocPreview(url, name);
                                    }
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: formatMarkdownToHtml(msg.reply),
                                  }}
                                />

                                {/* Referenced Documents Action Card (MLD-Style Preview Trigger) */}
                                {extractReferencedDocs(msg.reply).length >
                                  0 && (
                                  <div className="mt-3 pt-2.5 border-t border-slate-200/80 flex flex-col gap-1.5">
                                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                                      <FileText className="w-3.5 h-3.5 text-indigo-600" />
                                      <span>
                                        Referenced Documents (
                                        {
                                          extractReferencedDocs(msg.reply)
                                            .length
                                        }
                                        )
                                      </span>
                                      <span className="text-[9px] font-normal normal-case text-slate-400">
                                        Click to preview
                                      </span>
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                      {extractReferencedDocs(msg.reply).map(
                                        (doc, dIdx) => (
                                          <div
                                            key={dIdx}
                                            onClick={() =>
                                              handleOpenDocPreview(
                                                doc.url,
                                                doc.displayName,
                                              )
                                            }
                                            className="flex items-center justify-between gap-2 p-2 rounded-xl bg-slate-50 hover:bg-indigo-50/70 border border-slate-200/90 hover:border-indigo-300 transition-all shadow-[0_1px_2px_rgba(0,0,0,0.02)] cursor-pointer group/doc max-w-full min-w-0"
                                          >
                                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                              <div className="w-7 h-7 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0 font-bold text-[9px] border border-indigo-200 group-hover/doc:bg-indigo-600 group-hover/doc:text-white transition-colors">
                                                PDF
                                              </div>
                                              <div className="min-w-0 flex-1">
                                                <p
                                                  className="text-[11.5px] font-semibold text-slate-800 group-hover/doc:text-indigo-900 truncate"
                                                  title={doc.name}
                                                >
                                                  {doc.displayName}
                                                </p>
                                                <p className="text-[9.5px] text-slate-400 truncate font-mono">
                                                  {doc.name}
                                                </p>
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleOpenDocPreview(
                                                    doc.url,
                                                    doc.displayName,
                                                  );
                                                }}
                                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[10.5px] font-semibold shadow-xs transition-colors"
                                              >
                                                <Eye className="w-3 h-3" />
                                                <span>Preview</span>
                                              </button>
                                              <a
                                                href={doc.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                                className="p-1 text-slate-400 hover:text-indigo-600 rounded hover:bg-white"
                                                title="Open in new tab"
                                              >
                                                <ExternalLink className="w-3 h-3" />
                                              </a>
                                            </div>
                                          </div>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}

                            {msg.outputId && (
                              <div className="copilot-approval-bar">
                                <div className="copilot-approval-title">
                                  <span className="flex items-center gap-1.5">
                                    <FileCheck className="w-3.5 h-3.5 text-[#667eea]" />
                                    Draft Artifact ID:{" "}
                                    <code>{msg.outputId}</code>
                                  </span>
                                </div>

                                <div className="copilot-approval-buttons">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleApproveArtifact(msg.outputId)
                                    }
                                    className="copilot-btn-action copilot-btn-approve"
                                  >
                                    <CheckCircle2 className="w-3.5 h-3.5" />{" "}
                                    Approve
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRejectArtifact(msg.outputId)
                                    }
                                    className="copilot-btn-action copilot-btn-reject"
                                  >
                                    ✕ Reject
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleDownloadMarkdown(msg.outputId)
                                    }
                                    className="copilot-btn-action copilot-btn-download"
                                  >
                                    <Download className="w-3.5 h-3.5" />{" "}
                                    Markdown (.md)
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleExportDoc(msg.outputId)
                                    }
                                    className="copilot-btn-action copilot-btn-export"
                                  >
                                    <FileText className="w-3.5 h-3.5" /> Export
                                    (.doc)
                                  </button>
                                </div>

                                {approvalResults[msg.outputId] && (
                                  <div
                                    className={`text-[11.5px] font-mono px-2.5 py-1.5 rounded mt-1.5 ${
                                      approvalResults[msg.outputId].success
                                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                        : approvalResults[msg.outputId].error
                                          ? "bg-rose-50 text-rose-700 border border-rose-200"
                                          : "text-slate-500"
                                    }`}
                                  >
                                    {approvalResults[msg.outputId].message}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* AI Thinking Spinner */}
              {isBusy && (
                <div className="copilot-msg-enter flex items-center gap-2.5 p-3.5 bg-white border border-slate-200/90 rounded-2xl rounded-tl-sm w-fit shadow-sm">
                  <Loader2 className="w-4 h-4 text-[#667eea] animate-spin" />
                  <span className="text-xs text-slate-500 font-medium tracking-wide">
                    CalVant Copilot is thinking…
                  </span>
                </div>
              )}
            </div>

            {/* ── Composer / Input Box ── */}
            {activeRespPrompt ? (
              <div className="p-3.5 bg-slate-50 border-t border-slate-200/80 flex items-center justify-center gap-2 text-xs text-slate-500 font-medium select-none flex-shrink-0">
                <Sparkles className="w-3.5 h-3.5 text-[#667eea]" />
                <span>
                  Please complete your role responsibilities setup to activate
                  chat
                </span>
              </div>
            ) : (
              <div className="p-3 bg-white border-t border-slate-200/80 flex-shrink-0">
                <div className="flex items-end gap-2 bg-slate-50/80 hover:bg-slate-50 focus-within:bg-white border border-slate-200/90 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-500/15 rounded-2xl px-3 py-1.5 transition-all shadow-sm">
                  {/* Hidden PDF Upload Input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={handleFileUpload}
                  />

                  {/* Upload Document Button */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    title="Attach PDF document"
                    className="p-1.5 mb-0.5 text-slate-400 hover:text-indigo-600 transition-colors disabled:opacity-40 flex-shrink-0 hover:bg-slate-100 rounded-lg"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>

                  {/* Auto-Expanding Message Textarea without ugly Windows scrollbars */}
                  <textarea
                    ref={textareaRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about compliance, policies, risks…"
                    rows={1}
                    disabled={isBusy}
                    style={{
                      minHeight: "36px",
                      maxHeight: "140px",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      lineHeight: "1.45",
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                      overflowY: "hidden",
                    }}
                    className="flex-1 bg-transparent border-none resize-none text-xs text-slate-800 placeholder-slate-400 focus:outline-none leading-normal py-1.5 px-0.5 [&::-webkit-scrollbar]:hidden"
                  />

                  {/* Submit Button (Text 'Submit', arrow removed) */}
                  <button
                    type="button"
                    onClick={() => handleSendMessage()}
                    disabled={!inputValue.trim() || isBusy}
                    aria-label="Submit message"
                    className="px-3.5 py-1.5 mb-0.5 rounded-xl bg-gradient-to-br from-[#667eea] to-[#764ba2] hover:opacity-95 active:scale-95 disabled:bg-slate-200 disabled:from-slate-200 disabled:to-slate-200 disabled:shadow-none disabled:cursor-not-allowed text-white disabled:text-slate-400 flex items-center justify-center transition-all shadow-[0_2px_8px_rgba(102,126,234,0.3)] flex-shrink-0 text-xs font-semibold tracking-wide"
                  >
                    {isBusy ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>

                {/* Keyboard Shortcuts Hint (Clean Single Line) */}
                <div className="flex items-center mt-2 px-1 text-[10px] text-slate-400 select-none whitespace-nowrap">
                  <span className="text-slate-400">
                    Press{" "}
                    <kbd className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200/80 font-mono text-[9px] text-slate-600 font-semibold">
                      Enter
                    </kbd>{" "}
                    to submit ·{" "}
                    <kbd className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200/80 font-mono text-[9px] text-slate-600 font-semibold">
                      Shift+Enter
                    </kbd>{" "}
                    for new line
                  </span>
                </div>

                {/* Regulatory Advisory Note */}
                <div className="mt-2 pt-1.5 border-t border-slate-100 flex items-start gap-1.5 text-[9.5px] text-slate-400 select-none leading-tight">
                  <Shield className="w-3 h-3 text-[#667eea] flex-shrink-0 mt-0.5" />

                  <span className="min-w-0 whitespace-normal break-words">
                    <b className="text-slate-600 font-medium">Disclaimer:</b>{" "}
                    CalVant AI CoPilot acts as an assistant, provides
                    suggestions & guidance to users, not as an autonomous
                    decision engine. Human approvals for generative outputs are
                    required.
                  </span>
                </div>
              </div>
            )}

            {/* ── 3. CAL-69 FIRST-TIME RESPONSIBILITIES ONBOARDING VIEW (Directly below main AI Copilot header) ── */}
            {activeRespPrompt && isRespModalOpen && (
              <div className="absolute top-[52px] inset-x-0 bottom-0 z-20 flex flex-col bg-white overflow-hidden animate-in fade-in duration-200">
                {/* Page Title & Scope Context Banner (Right below AI Copilot Header) */}
                <div className="px-4 py-2.5 bg-gradient-to-r from-slate-50 to-indigo-50/50 border-b border-slate-200/90 select-none flex-shrink-0">
                  <h3 className="text-xs font-bold text-slate-800 tracking-tight truncate whitespace-nowrap">
                    Setup Your Governance & Compliance Scope
                  </h3>
                  <p className="text-[10px] text-slate-500 font-medium truncate whitespace-nowrap mt-0.5">
                    Select the operational duties that apply to your role
                  </p>
                </div>

                {/* Subheader Context Banner */}
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-200/80 flex items-center justify-between gap-3 flex-shrink-0 select-none">
                  <div className="text-[11.5px] text-slate-700 font-medium leading-relaxed truncate">
                    {activeRespPrompt.header ||
                      "Please select the responsibilities that apply to your role:"}
                  </div>
                  <button
                    type="button"
                    onClick={handleSelectAllResp}
                    className="text-[11px] font-semibold text-[#667eea] hover:text-[#764ba2] px-2 py-0.5 rounded-md hover:bg-slate-100 transition-colors whitespace-nowrap"
                  >
                    {selectedRespIds.length === respItems.length
                      ? "Clear All"
                      : "Select All"}
                  </button>
                </div>

                {/* Scrollable Responsibilities Checklist */}
                <div className="flex-1 overflow-y-auto p-3.5 sm:p-4 space-y-2 bg-slate-50/50 copilot-scroll">
                  {respItems.map((item) => {
                    const isSelected = selectedRespIds.includes(item.id);
                    const isEditing = editingItemId === item.id;

                    return (
                      <div
                        key={item.id}
                        className={`flex items-start gap-2.5 p-3 rounded-xl border transition-all duration-150 ${
                          isSelected
                            ? "bg-[#667eea]/5 border-[#667eea] ring-1 ring-[#667eea]/30 shadow-xs"
                            : "bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50/60 shadow-2xs"
                        }`}
                      >
                        {/* Checkbox (ALWAYS explicitly visible with 2px crisp border) */}
                        <div
                          onClick={() => toggleRespItem(item.id)}
                          className={`w-5 h-5 min-w-[20px] min-h-[20px] mt-0.5 rounded-[5px] flex items-center justify-center cursor-pointer transition-all flex-shrink-0 select-none ${
                            isSelected
                              ? "bg-gradient-to-r from-[#667eea] to-[#764ba2] border-2 border-[#667eea] text-white shadow-xs"
                              : "border-2 border-slate-300 bg-white hover:border-[#667eea] hover:bg-slate-50"
                          }`}
                        >
                          {isSelected && (
                            <Check className="w-3.5 h-3.5 stroke-[3] text-white" />
                          )}
                        </div>

                        {/* Content Area */}
                        <div className="flex-1 min-w-0">
                          {isEditing ? (
                            <div
                              className="flex items-center gap-1.5"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="text"
                                value={editingItemText}
                                onChange={(e) =>
                                  setEditingItemText(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter")
                                    handleSaveEdit(e, item.id);
                                  if (e.key === "Escape") handleCancelEdit(e);
                                }}
                                className="flex-1 text-xs px-2.5 py-1 border border-[#667eea] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#667eea]/20 text-slate-800 bg-white"
                                autoFocus
                              />
                              <button
                                type="button"
                                onClick={(e) => handleSaveEdit(e, item.id)}
                                title="Save changes"
                                className="p-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs transition-colors"
                              >
                                <Check className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={handleCancelEdit}
                                title="Cancel"
                                className="p-1.5 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 transition-colors border border-gray-300"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-start justify-between gap-2">
                              <div className="space-y-0.5 select-text">
                                <div
                                  onClick={() => {
                                    if (
                                      typeof window !== "undefined" &&
                                      window.getSelection()?.toString()
                                        .length === 0
                                    ) {
                                      toggleRespItem(item.id);
                                    }
                                  }}
                                  className="font-medium text-xs sm:text-[12.5px] text-slate-800 leading-snug cursor-text select-text"
                                >
                                  {item.title}
                                </div>
                                <span
                                  className={`inline-block text-[9.5px] font-mono px-1.5 py-0.2 rounded border select-text ${
                                    isSelected
                                      ? "bg-[#667eea]/10 text-[#667eea] border-[#667eea]/20"
                                      : "bg-slate-100 text-slate-500 border-slate-200/80"
                                  }`}
                                >
                                  {item.id}
                                </span>
                              </div>

                              {/* Action: Inline Edit Icon */}
                              <button
                                type="button"
                                onClick={(e) => handleStartEdit(e, item)}
                                title="Edit this responsibility"
                                className="p-1.5 rounded-md text-slate-400 hover:text-[#667eea] hover:bg-[#667eea]/10 transition-colors flex-shrink-0"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* ── "+ Add Custom Responsibility" Section ── */}
                  {isAddingCustom ? (
                    <div className="p-3 bg-[#667eea]/5 rounded-xl border border-[#667eea]/30 space-y-2 animate-in fade-in duration-150">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                          <Plus className="w-3.5 h-3.5 text-[#667eea]" />
                          New Custom Responsibility
                        </span>
                        <span className="text-[10.5px] text-[#667eea] font-medium">
                          (Will be added to your scope)
                        </span>
                      </div>
                      <textarea
                        value={customInputText}
                        onChange={(e) => setCustomInputText(e.target.value)}
                        placeholder="Type your custom responsibility here (e.g. Cloud infrastructure audits, SOC2 reporting, quarterly risk review)…"
                        rows={2}
                        className="w-full text-xs p-2 bg-white border border-[#667eea]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#667eea]/20 focus:border-[#667eea] text-slate-800 placeholder-slate-400 resize-none leading-relaxed"
                        autoFocus
                      />
                      <div className="flex items-center justify-end gap-2 pt-0.5">
                        <button
                          type="button"
                          onClick={() => {
                            setIsAddingCustom(false);
                            setCustomInputText("");
                          }}
                          className="px-3.5 py-1.5 text-xs font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 rounded-lg border border-gray-300 transition-colors shadow-xs"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleAddCustomItem}
                          disabled={!customInputText.trim()}
                          className="px-4 py-1.5 text-xs font-semibold bg-gradient-to-r from-[#667eea] to-[#764ba2] hover:opacity-95 text-white rounded-lg shadow-xs disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1.5"
                        >
                          <Check className="w-3.5 h-3.5" />
                          <span>Update</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddingCustom(true);
                        setCustomInputText("");
                      }}
                      className="w-full flex items-center justify-center gap-2 p-2.5 rounded-xl border border-dashed border-[#667eea]/40 bg-[#667eea]/5 hover:bg-[#667eea]/10 hover:border-[#667eea] text-[#667eea] text-xs font-semibold transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>+ Add Custom Responsibility</span>
                    </button>
                  )}
                </div>

                {/* Modal Sticky Bottom Bar */}
                <div className="p-3.5 px-5 bg-white border-t border-slate-200/90 flex items-center justify-between gap-3 select-none flex-shrink-0">
                  <div className="text-xs text-slate-600 font-medium">
                    {selectedRespIds.length > 0 ? (
                      <span>
                        <b className="text-[#667eea] font-bold">
                          {selectedRespIds.length}
                        </b>{" "}
                        {selectedRespIds.length === 1
                          ? "responsibility"
                          : "responsibilities"}{" "}
                        selected
                      </span>
                    ) : (
                      <span className="text-slate-400">
                        {isAddingCustom
                          ? "Complete custom responsibility above"
                          : "Select at least 1 responsibility"}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleSendResponsibilities}
                      disabled={
                        isAddingCustom ||
                        editingItemId !== null ||
                        selectedRespIds.length === 0 ||
                        isBusy
                      }
                      className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#667eea] to-[#764ba2] shadow-[0_4px_15px_rgba(102,126,234,0.35)] hover:shadow-[0_8px_22px_rgba(102,126,234,0.45)] active:scale-95 text-white font-semibold text-xs sm:text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                      {isBusy ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          <span>Submitting…</span>
                        </>
                      ) : (
                        <span>Submit </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── 3. PROFESSIONAL INTEGRATED WORKSPACE DOCUMENT VIEWER (NO POPUP MODAL) ── */}
      {previewModalOpen && isOpen && (
        <>
          {/* A. Collapsed Dashboard Strip (Clean Light Line matching website - NO BLACK BARS) */}
          {isDashboardCollapsed && (
            <div
              onClick={() => {
                setIsDashboardCollapsed(false);
                setIsDocCollapsed(true);
              }}
              title="Click arrow to expand Dashboard (PDF will collapse)"
              className={`fixed z-[99990] flex flex-col items-center justify-between py-3.5 w-[28px] sm:w-[30px] bg-white hover:bg-indigo-50/40 text-slate-700 border border-slate-200/90 shadow-md rounded-xl cursor-pointer transition-all group select-none ${
                isAdminPage
                  ? "top-3 bottom-3"
                  : "top-[84px] sm:top-[88px] bottom-3.5"
              } left-[60px] sm:left-[68px]`}
            >
              {/* Expand Action Arrow Button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDashboardCollapsed(false);
                  setIsDocCollapsed(true);
                }}
                className="w-6 h-6 rounded-lg bg-indigo-50 group-hover:bg-indigo-600 text-indigo-600 group-hover:text-white flex items-center justify-center transition-all shadow-2xs"
                title="Expand Dashboard (Collapse PDF)"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>

              {/* Clean Vertical Label & Icon */}
              <div className="flex flex-col items-center gap-2.5">
                <LayoutDashboard className="w-3.5 h-3.5 text-slate-400 group-hover:text-indigo-600 transition-colors" />
                <span
                  style={{
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                  }}
                  className="text-[9px] font-bold tracking-widest text-slate-400 group-hover:text-indigo-700 uppercase transition-colors"
                >
                  Dashboard
                </span>
              </div>

              {/* Status Dot */}
              <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
            </div>
          )}

          {/* B. Collapsed Document Strip (Clean Light Line next to Copilot) */}
          {isDocCollapsed && (
            <div
              onClick={() => {
                setIsDocCollapsed(false);
                setIsDashboardCollapsed(true);
              }}
              title="Click arrow to expand Document Viewer (Dashboard will collapse)"
              className={`fixed z-[99990] flex flex-col items-center justify-between py-3.5 w-[28px] sm:w-[30px] bg-white hover:bg-indigo-50/40 text-slate-700 border border-slate-200/90 shadow-md rounded-xl cursor-pointer transition-all group select-none ${
                isAdminPage
                  ? "top-3 bottom-3"
                  : "top-[84px] sm:top-[88px] bottom-3.5"
              } right-[395px]`}
            >
              {/* Expand Button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDocCollapsed(false);
                  setIsDashboardCollapsed(true);
                }}
                className="w-6 h-6 rounded-lg bg-indigo-50 group-hover:bg-indigo-600 text-indigo-600 group-hover:text-white flex items-center justify-center transition-all shadow-2xs"
                title="Expand Document Viewer"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>

              {/* Document Icon & Vertical Title */}
              <div className="flex flex-col items-center gap-2.5 min-h-0 flex-1 justify-center py-2">
                <FileText className="w-3.5 h-3.5 text-indigo-500" />
                <span
                  style={{
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                    maxHeight: "180px",
                  }}
                  className="text-[9px] font-bold tracking-widest text-slate-400 group-hover:text-indigo-700 uppercase truncate"
                >
                  Document
                </span>
              </div>

              {/* Close Button on Strip */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  closePreviewModal();
                }}
                className="w-5 h-5 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-colors"
                title="Close Document"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* C. Active Document Workspace Window (True Side-by-side or Full Workspace) */}
          {!isDocCollapsed && (
            <div
              className={`fixed z-[99990] flex flex-col bg-white rounded-2xl border border-slate-200/90 shadow-xl transition-all duration-200 ${
                isAdminPage
                  ? "top-3 bottom-3"
                  : "top-[84px] sm:top-[88px] bottom-3.5"
              }`}
              style={{
                right: "395px",
                left: isDashboardCollapsed ? "calc(68px + 38px)" : undefined,
                width: !isDashboardCollapsed ? `${docWidthPx}px` : undefined,
                maxWidth: "calc(100vw - 480px)",
              }}
            >
              {/* ALWAYS-VISIBLE Divider Handle on Left Border */}
              <div
                onMouseDown={handleSplitterMouseDown}
                title="Drag to resize panes or click arrows to expand/collapse"
                className="absolute top-0 bottom-0 -left-3 w-6 cursor-col-resize z-50 flex items-center justify-center group select-none"
              >
                {/* Subtle vertical line indicator */}
                <div className="w-1 h-full bg-slate-200 group-hover:bg-indigo-400 rounded-full transition-colors shadow-2xs" />

                {/* Floating Collapse Arrow Pill Badge - ALWAYS VISIBLE! */}
                <div className="absolute top-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5 p-1 bg-white border border-slate-200 shadow-md rounded-full group-hover:border-indigo-400 group-hover:shadow-lg transition-all scale-100 hover:scale-105">
                  {/* Left arrow: Collapse dashboard & expand document to full workspace */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDashboardCollapsed(true);
                      setIsDocCollapsed(false);
                    }}
                    className="w-5 h-5 rounded-full hover:bg-indigo-50 text-slate-500 hover:text-indigo-600 flex items-center justify-center transition-colors"
                    title="Focus Document (Collapse Dashboard)"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>

                  {/* Drag grip icon */}
                  <GripVertical className="w-2.5 h-2.5 text-slate-400 group-hover:text-indigo-500" />

                  {/* Right arrow: Collapse PDF & Focus Dashboard */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDocCollapsed(true);
                      setIsDashboardCollapsed(false);
                    }}
                    className="w-5 h-5 rounded-full hover:bg-indigo-50 text-slate-500 hover:text-indigo-600 flex items-center justify-center transition-colors"
                    title="Expand Dashboard (Collapse Document)"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Clean White Document Header Bar (Matching CalVant Theme) */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-slate-200/90 select-none flex-shrink-0 gap-2 rounded-t-2xl">
                {/* Left: Document Badge & Title */}
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px] font-bold px-2 py-0.5 rounded shadow-2xs flex-shrink-0">
                    PDF
                  </span>
                  <span
                    className="text-xs font-semibold text-slate-800 truncate"
                    title={previewDocName}
                  >
                    {previewDocName || "Document Viewer"}
                  </span>
                </div>

                {/* Center: Split View Quick Toggle */}
                <div className="hidden sm:flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg p-0.5">
                  <button
                    type="button"
                    onClick={() => {
                      if (isDashboardCollapsed || isDocCollapsed) {
                        setIsDashboardCollapsed(false);
                        setIsDocCollapsed(false);
                        const { availableWidth } = getWorkspaceDimensions();
                        setDocWidthPx(Math.round(availableWidth * 0.5));
                      } else {
                        // Already in split view -> switch to full document focus
                        setIsDashboardCollapsed(true);
                        setIsDocCollapsed(false);
                      }
                    }}
                    className={`px-2 py-1 rounded text-[10.5px] font-medium flex items-center gap-1 transition-colors ${
                      !isDashboardCollapsed && !isDocCollapsed
                        ? "bg-white text-indigo-700 shadow-2xs font-semibold"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                    title="Toggle 50/50 Split View with Dashboard"
                  >
                    <Columns className="w-3 h-3" />
                    <span>Split View</span>
                  </button>
                </div>

                {/* Right: Open in New Tab & Close */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-50 hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 border border-slate-200 hover:border-indigo-200 text-xs font-semibold transition-colors"
                    title="Open document in a new browser tab"
                  >
                    <span>Open in New Tab</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>

                  <button
                    type="button"
                    onClick={closePreviewModal}
                    className="w-7 h-7 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-colors font-bold text-sm"
                    title="Close document viewer and restore full dashboard"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Document Content Iframe (Shows browser top toolbar with page count, hides black sidebar) */}
              <div className="flex-1 w-full h-full relative bg-white overflow-hidden rounded-b-2xl">
                <iframe
                  src={
                    previewUrl
                      ? previewUrl.includes("#")
                        ? previewUrl
                        : `${previewUrl}#navpanes=0&view=FitH`
                      : ""
                  }
                  title="Document Preview"
                  className="w-full h-full border-0 bg-white"
                />
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}